import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Control, Controller } from 'react-hook-form';
import { RangeSlider, type RangeSliderProps } from '@/components/ui/range-slider';

import { X } from 'lucide-react';

import { isEqual } from 'lodash';
import { cn } from '@client/lib';

export function CustomRangeSlider({
  name,
  control,
  label,
  size = 'default',
  testId,
  ...props
}: RangeSliderProps & {
  name: string;
  label?: string;
  control: Control<any>;
  size?: 'sm' | 'default';
  testId?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const valueArray = Array.isArray(value) ? value : [0, 100];
        const isDefault = isEqual(valueArray, props.defaultValue);

        return (
          <div className="space-y-4">
            <div
              className={cn('flex flex-wrap items-center justify-between gap-4', {
                'gap-2': size === 'sm',
              })}
            >
              {label && (
                <Label
                  className={cn('text-primary-dark text-lg font-semibold', {
                    'text-sm': size === 'sm',
                  })}
                >
                  {label}
                </Label>
              )}
              <Button
                size={size === 'sm' ? 'icon' : 'sm'}
                variant={size === 'sm' ? 'ghost' : 'outline'}
                onClick={() => onChange(props.defaultValue)}
                className={cn('border-primary/25 text-primary-dark ml-auto h-7 px-2 text-xs', {
                  'size-5 p-0': size === 'sm',
                  'invisible': isDefault,
                })}
              >
                {size === 'sm' ? <X className="size-4" /> : 'Reset'}
              </Button>
            </div>
            <RangeSlider
              {...props}
              data-testid={testId}
              value={valueArray}
              onValueChange={value => {
                props?.onValueChange?.(value);
                onChange(value);
              }}
              className="[&_[data-slot=slider-thumb]]:size-4 [&_[data-slot=slider-track]]:h-1.5"
            />
          </div>
        );
      }}
    />
  );
}
